import React, { useEffect, useState, useRef } from 'react'
import PropTypes from 'prop-types'
import { uuid } from 'uuidv4'
import { get, sortBy, filter, some, intersectionBy, reduce, head, isEmpty, map, uniqBy } from 'lodash'
import cn from 'classnames'

import { ReactComponent as IconCloseSm } from '../../assets/icons/IconCloseSm.svg'
import { roleInfo } from '../../utils/employeeRoles'

import Modal from '../Modal'
import Loader from '../Loader'

const SuiteEdit = ({
  anonymousCustomerExists,
  attendants,
  attendantSettings,
  createAttendantSetting,
  createCustomerOrderingPreferences,
  currentVenueUuid,
  customerDefaultPreferences,
  customerDefaultPreferenceUuids,
  customers,
  editPage,
  employeeRoles,
  eventId,
  gloabalAttendantSettings,
  history,
  isLoading,
  loadAllCustomerOrderingPreferencesByEventId,
  loadAttendantSettings,
  loadCustomers,
  loadPreOrders,
  loadRevenueCenters,
  loadSuitesStaff,
  newlyCreatedCustomerId,
  newlyRemovedCustomerId,
  pendingCustomerOrderingPreferenceIds,
  removeCustomerOrderingPreference,
  suites,
  updateAttendantSetting,
}) => {
  const [filterQuery, setFilterQuery] = useState('')
  const [isListVisible, setIsListVisible] = useState(false)
  const modalSubtitle = suites.map((suite) => suite.name).join(', ')

  const userListRef = useRef(null)
  const inputRef = useRef(null)

  useEffect(() => {
    if (!isEmpty(currentVenueUuid)) {
      loadPreOrders(currentVenueUuid)
    }
  }, [loadPreOrders, currentVenueUuid])

  useEffect(() => {
    if (!isEmpty(currentVenueUuid)) {
      loadSuitesStaff(currentVenueUuid)
    }
  }, [loadSuitesStaff, currentVenueUuid])

  useEffect(() => {
    loadRevenueCenters(currentVenueUuid)
  }, [currentVenueUuid, loadRevenueCenters])

  useEffect(() => {
    loadCustomers(currentVenueUuid)
  }, [currentVenueUuid, loadCustomers])

  useEffect(() => {
    loadAllCustomerOrderingPreferencesByEventId()
  }, [loadAllCustomerOrderingPreferencesByEventId, newlyCreatedCustomerId, newlyRemovedCustomerId])

  useEffect(() => {
    if (!isEmpty(currentVenueUuid)) {
      loadAttendantSettings(currentVenueUuid)
    }
  }, [loadAttendantSettings, currentVenueUuid])

  const handleClickOutside = e => {
    if (userListRef.current.contains(e.target) || inputRef.current.contains(e.target)) {
      return
    }
    setIsListVisible(false)
    setFilterQuery('')
  }

  useEffect(
    () => {
      if (isListVisible) {
        document.addEventListener('mousedown', handleClickOutside)
      } else {
        document.removeEventListener('mousedown', handleClickOutside)
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    },
    // eslint-disable-next-line
    [isListVisible],
  )

  const assignOrRemoveAttendant = (action, attendantUuid) => {
    // update attendantSetting
    const globalAttendantSetting = get(gloabalAttendantSettings, attendantUuid) || {}
    const globalMenuSubtypes = get(globalAttendantSetting, 'configuration.menuSubtypes') || []
    const attendantSetting = get(attendantSettings, attendantUuid) || {}
    const isNew = isEmpty(attendantSetting)
    const attendantSettingStandUuids = get(attendantSetting, 'configuration.standUuids', [])
    const attendantMenuSubtypes = get(attendantSetting, 'configuration.menuSubtypes', [])
    const standUuids = reduce(suites, (uuids, suite) => [ ...uuids, get(suite, 'rvcUuid')], [])
    let updatedUuids

    if (action === 'assign') {
      updatedUuids = [
        ...attendantSettingStandUuids,
        ...standUuids
      ]
    } else if (action === 'remove') {
      updatedUuids = attendantSettingStandUuids.filter(uuid => !standUuids.includes(uuid))
    }

    const attendantSettingParams = {
      ...attendantSetting,
        uuid: isNew ? uuid() : get(attendantSetting, 'uuid', ''),
        configuration: {
          ...attendantSetting.configuration,
          menuSubtypes: isNew ? globalMenuSubtypes : attendantMenuSubtypes,
          standUuids: updatedUuids || attendantSettingStandUuids
        },
        salesEventId: eventId,
        employeeUuid: attendantUuid,
        type: 'AppDeviceConfiguration::AttendantSetting',
    }

    isNew ? createAttendantSetting(currentVenueUuid, attendantSettingParams) : updateAttendantSetting(attendantSettingParams)
  }

  const assignOrRemoveCustomer = (action, id) => {
    if (action === 'assign') {
      const params =  reduce(suites, (payloads, suite) => {
        const newUuid = uuid()

        return [ ...payloads, {
          uuid: newUuid,
          customerId: id,
          standUuid: get(suite, 'rvcUuid'),
          salesEventId: eventId,
          type: "Customer::StandPreference"
        }]
      }, [])

      createCustomerOrderingPreferences(id, params)
    } else if (action === 'remove') {
      removeCustomerOrderingPreference(id)
    }
  }

  const createAssignedAndFilteredItems = (collection, type, typeId, typeName) => {
    const assignedItems = sortBy(
      reduce(
        suites,
        (common, suite) => {
          const suiteItems = get(suite, type, [])
          const suiteItemsAddPending = map(suiteItems, item => ({
            ...item,
            isPending: pendingCustomerOrderingPreferenceIds.includes(item.preferenceUuid)
          }))

          if (!common.length) return suiteItemsAddPending

          return intersectionBy(common, suiteItemsAddPending, typeId)
        },
        []
      ),
      typeName
    )

    const filteredItems = filter(collection, (item) => {
      if (!filterQuery) return false

      const itemName = (item?.name ?? '').toUpperCase()
      const meetsQueryRequirements =  itemName.includes(filterQuery.toUpperCase())

      return !some(assignedItems, {[typeId]: item.uuid, [typeName]: item.name}) && meetsQueryRequirements
    })

    return [assignedItems, filteredItems]
  }

  const [assignedCustomers, filteredCustomers] = createAssignedAndFilteredItems(customers, 'customers', 'customerId', 'customerName')
  const [assignedAttendants, filteredAttendants] = createAssignedAndFilteredItems(attendants, 'attendants', 'attendantUuid', 'attendantName')

  const displayVariables = {
    customers: {
      assignedItems: assignedCustomers,
      filteredItems: filteredCustomers,
      id: 'customerId',
      name: 'customerName',
      redirect: item => `/events/${eventId}/${get(head(suites), 'rvcUuid')}/${item['customerId']}`,
      preferenceId: 'preferenceUuid',
      onClick: assignOrRemoveCustomer,
      title: 'Customer',
    },
    attendants: {
      assignedItems: assignedAttendants,
      filteredItems: filteredAttendants,
      id: 'attendantUuid',
      name: 'attendantName',
      redirect: item => `/suites/menus/${eventId}/${item['attendantUuid']}`,
      preferenceId: 'attendantUuid',
      onClick: assignOrRemoveAttendant,
      title: 'Attendant',
    }
  }

  // if we have customers/attendants from multiple suites we do not redirect
  if (suites.length > 1) {
    displayVariables.customers.redirect = () => {}
    displayVariables.attendants.redirect = () => {}
  }

  const pageInfo = editPage === 'customers' ? displayVariables.customers : displayVariables.attendants
  const { assignedItems, filteredItems, id, name, preferenceId, onClick, redirect, title } = pageInfo

  const filterInfo = {
    title: editPage === 'customers' ? "Customers" : "Staff",
  }

  // filtering by roles
  let listFiltered, listAssigned

  if (editPage === 'attendants') {
    const filteredItemsWithRole = map(filteredItems, item => {
      const legacyRole = employeeRoles?.[item?.employeeId]?.role
      const role = isEmpty(legacyRole) ? employeeRoles?.[item?.id]?.role : legacyRole

      return { ...item, roleName: roleInfo?.[role]?.name, roleRef: role }
    })

    listFiltered = filter(filteredItemsWithRole, item => {
      return (item?.roleName === 'Suite Attendant' || item?.roleName === 'Cart Attendant') && !item?.deactivatedAt
    })

    const assignedItemsWithRoles = map(uniqBy(assignedItems, 'employeeId'), item => {
      const legacyRole = employeeRoles?.[item?.employeeId]?.role
      const role = isEmpty(legacyRole) ? employeeRoles?.[item?.roleId]?.role : legacyRole

      return { ...item, roleName: roleInfo?.[role]?.name, roleRef: role }
    })

    listAssigned = filter(assignedItemsWithRoles, item => {
      return item?.roleName === 'Suite Attendant' || item?.roleName === 'Cart Attendant'
    })
  } else {
    listFiltered = filter(filteredItems, filteredItem => customerDefaultPreferenceUuids.includes(filteredItem.uuid))
    listAssigned = assignedItems
  }

  const listAssignedUuids = map(listAssigned, item => item.customerId)

  const assignedAndUnassignedCustomersDifference = filter(customerDefaultPreferences, customerDefaultPreference => {
    return !listAssignedUuids.includes(customerDefaultPreference.uuid)
  })

  if(!anonymousCustomerExists){
    assignedAndUnassignedCustomersDifference.push({ name: 'Anonymous', uuid: uuid() })
  }

  const [itemsList, setItemsList] = useState([])

  const handleClick = uuid => {
    const filtered = filter(itemsList, filteredItem => filteredItem.uuid !== uuid)

    setItemsList(filtered)
    onClick("assign", uuid)
    setIsListVisible(false)
    setFilterQuery('')
  }

  useEffect(() => {
    setItemsList(listFiltered)
    // eslint-disable-next-line
  }, [filterQuery])

  const redirectToDetails = (item, isAnonymous) => isAnonymous ? () => {} : history.push(redirect(item))

  const sortedListAssignedWithSuiteAttendantsBeforeCartAttendants = (array) => {
    if (editPage === 'customers') return array

    return array.map((item) => {
      const legacyRole = employeeRoles?.[item?.employeeId]?.role
      const newRole = isEmpty(legacyRole) ? employeeRoles?.[item?.roleId]?.role : legacyRole
      const role = roleInfo?.[newRole]?.name

      return {
        ...Object.assign(item, {role})
      }
    })
    .sort((cart, suite) => {
      return suite.role.localeCompare(cart.role)
    })
  }

  const displayEditPage = () => (
    <div>
      <div className={cn('suite-edit', { '-isSearchOpen': isListVisible })}>
        <div className="suite-edit__filter">
          <div className="suite-edit__filter-container">
            <div className="suite-edit__filter-input-container">
              <input
                ref={inputRef}
                type="text"
                className="suite-edit__filter-input"
                value={filterQuery}
                onChange={event => setFilterQuery(event.target.value)}
                onFocus={() => setIsListVisible(true)}
                placeholder={`Search for ${filterInfo.title}...`}
              />
            </div>
          </div>
        </div>
        {!isListVisible ? (
          <>
            {
              <>
                <header>
                  <p className="suite-edit__subheader">{`Assigned ${filterInfo.title}:`}</p>
                </header>
                <div>
                  {
                    sortedListAssignedWithSuiteAttendantsBeforeCartAttendants(listAssigned).map((item, index) => (
                      <div key={index} className="suite-edit__customer">
                        <span
                          onClick={() => { redirectToDetails(item, isEmpty(item[id])) } }
                        >
                          <span>
                          {item[id] ? item[name] : "Anonymous"}
                          </span>
                          {!!item?.roleName && <div className="suite-edit-employee-role" style={{ backgroundColor: roleInfo?.[item?.roleRef]?.color }}>
                            <span>{item.roleName}</span>
                          </div>}
                        </span>
                        {(item[preferenceId] && !item.isPending) && (
                          <>
                          <span
                            className="suite-edit__customer-remove"
                            onClick={() => onClick("remove", item[preferenceId])}
                          >
                            <IconCloseSm id={item[preferenceId]} />
                          </span>
                          </>
                        )}
                      </div>
                    ))
                  }
                </div>
              </>
            }
            {editPage === 'customers' && (
              <section>
                <header>
                  <p className="suite-edit__subheader">{`Unassigned ${filterInfo.title}:`}</p>
                </header>
                <div>
                  {map(assignedAndUnassignedCustomersDifference, customerDefaultPreference => {
                    return (
                      <div
                        className="suite-edit__customer"
                        key={customerDefaultPreference.uuid}
                      >
                        <div className="suite-edit__add-unassigned">
                          <span className="suite-edit__customer-name">
                            {customerDefaultPreference.name}
                          </span>
                          <span
                            className="suite-edit__customer-add"
                            onClick={() => onClick("assign", customerDefaultPreference.uuid)}
                          >
                            +
                          </span>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </section>
            )}
          </>
        ) : (
          <div className="suite-edit__add-stuff-list" ref={userListRef}>
            {sortBy(itemsList, "name").map(item => (
              <div
                key={item.uuid}
                id={item.uuid}
                className="suite-edit__customer-list"
                onClick={() => handleClick(item.uuid)}
              >
              <span>
                {item.name}
              </span>
              {!!item?.roleName && <div className="suite-edit-employee-role" style={{ backgroundColor: roleInfo?.[item?.roleRef]?.color }}>
                  <span>{item.roleName}</span>
              </div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )

  return (
    <Modal
      title={`${title} Assignments`}
      onClose={() => history.push(`/events/${eventId}`)}
      subtitle={modalSubtitle}
    >
      { isLoading ? <Loader /> : displayEditPage() }
    </Modal>
  )
}

SuiteEdit.defaultProps = {
  currentVenueUuid: '',
  customers: [],
  customerDefaultPreferences: [],
  customerDefaultPreferenceUuids: [],
  createCustomerOrderingPreferences: () => {},
  eventId: '',
  isLoading: false,
  loadAllCustomerOrderingPreferencesByEventId: () => {},
  loadCustomers: () => {},
  loadPreOrders: () => {},
  loadRevenueCenters: () => {},
  removeCustomerOrderingPreference: () => {},
  suites: {},
  pendingCustomerOrderingPreferenceIds: [],
  anonymousCustomerExists: false,
  gloabalAttendantSettings: {},
}

SuiteEdit.propTypes = {
  currentVenueUuid: PropTypes.string,
  customers: PropTypes.arrayOf(PropTypes.object),
  customerDefaultPreferences: PropTypes.arrayOf(PropTypes.object),
  customerDefaultPreferenceUuids: PropTypes.array,
  createCustomerOrderingPreferences: PropTypes.func,
  eventId: PropTypes.string,
  isLoading: PropTypes.bool,
  loadAllCustomerOrderingPreferencesByEventId: PropTypes.func,
  loadCustomers: PropTypes.func,
  loadPreOrders: PropTypes.func,
  loadRevenueCenters: PropTypes.func,
  newlyCreatedCustomerId: PropTypes.string,
  removeCustomerOrderingPreference: PropTypes.func,
  suites: PropTypes.array,
  pendingCustomerOrderingPreferenceIds: PropTypes.array,
  anonymousCustomerExists: PropTypes.bool,
  gloabalAttendantSettings: PropTypes.object,
}

export default SuiteEdit
